import { useState } from 'react';
import { CheckCircle2, Clock3, Flame, Heart, Sparkles, XCircle } from 'lucide-react';
import { ArcadeSummaryView } from '../components/ui';
import {
  abandonSurvivalRun,
  answerSurvivalQuestion,
  completeSurvivalRun,
  createSurvivalRun,
  getSurvivalRun,
  type ArcadeAnswerResult,
  type ArcadeCompleteResult,
  type ArcadeRun,
  type ArcadeRunState,
} from '../api/learning';
import './SurvivalGame.css';

type SurvivalBoard = ArcadeRun | ArcadeRunState;

type PendingAnswer = { questionId: string; answer: string };

export function SurvivalGame() {
  const [run, setRun] = useState<SurvivalBoard | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answeredIds, setAnsweredIds] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingAnswer | null>(null);
  const [grade, setGrade] = useState<ArcadeAnswerResult | null>(null);
  const [summary, setSummary] = useState<ArcadeCompleteResult | null>(null);
  const [streakDays, setStreakDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'start' | 'grade' | 'recover' | null>(null);

  const question = run?.questions.find(item => !answeredIds.includes(item.id)) ?? null;

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const created = await createSurvivalRun();
      setRun(created);
      setAnswerText('');
      setAnsweredIds([]);
      setPending(null);
      setGrade(null);
      setSummary(null);
      setStreakDays(created.streakDays);
    } catch {
      setError('start');
    } finally {
      setBusy(false);
    }
  }

  function applyGrade(result: ArcadeAnswerResult) {
    setRun(current =>
      current
        ? {
            ...current,
            score: result.score,
            combo: result.combo,
            bestCombo: result.bestCombo,
            deadlineAt: result.deadlineAt,
            status: result.status,
            personalBest: result.personalBest,
            livesRemaining: result.livesRemaining,
          }
        : current
    );
    setAnsweredIds(current =>
      current.includes(result.questionId) ? current : [...current, result.questionId]
    );
    setAnswerText('');
    setPending(null);
    setGrade(result);
    setError(null);
    if (result.complete && result.verifiedScore !== null) {
      setSummary({
        runId: run?.runId ?? '',
        status: result.status,
        verifiedScore: result.verifiedScore,
        score: result.score,
        bestCombo: result.bestCombo,
        correctCount: result.correctCount,
        answeredCount: result.answeredCount,
        accuracy: result.accuracy,
        personalBest: result.personalBest ?? result.verifiedScore,
        isNewPersonalBest: result.isNewPersonalBest,
        streakDays,
        ghostOutcome: null,
      });
    }
  }

  async function submitAnswer(answer: string) {
    if (!run || !question || busy) return;
    const next = { questionId: question.id, answer };
    setPending(next);
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerSurvivalQuestion(run.runId, question.id, answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function retry() {
    if (!run || !pending) return;
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerSurvivalQuestion(run.runId, pending.questionId, pending.answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      const recovered = await getSurvivalRun(run.runId);
      setRun(recovered);
      setAnsweredIds(recovered.gradedQuestionIds);
      setAnswerText('');
      setPending(null);
      if (recovered.status !== 'in_progress' && recovered.verifiedScore !== null) {
        setSummary({
          runId: recovered.runId,
          status: recovered.status,
          verifiedScore: recovered.verifiedScore,
          score: recovered.score,
          bestCombo: recovered.bestCombo,
          correctCount: recovered.correctCount,
          answeredCount: recovered.answeredCount,
          accuracy: recovered.accuracy,
          personalBest: recovered.personalBest ?? recovered.verifiedScore,
          isNewPersonalBest: recovered.isNewPersonalBest,
          streakDays,
          ghostOutcome: null,
        });
      }
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  async function abandon() {
    if (!run) return;
    setBusy(true);
    try {
      await abandonSurvivalRun(run.runId);
      setRun(null);
      setAnswerText('');
      setAnsweredIds([]);
      setPending(null);
      setGrade(null);
      setError(null);
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  if (summary) {
    return (
      <ArcadeSummaryView
        heading="Game over"
        verifiedScore={summary.verifiedScore}
        accuracy={summary.accuracy}
        correctCount={summary.correctCount}
        answeredCount={summary.answeredCount}
        isNewPersonalBest={summary.isNewPersonalBest}
        additionalStats={[{ label: 'Best combo', value: `×${summary.bestCombo}` }]}
        playAgainDisabled={busy}
        onPlayAgain={() => void start()}
      />
    );
  }

  if (!run) {
    return (
      <section aria-labelledby="survive-heading">
        <div className="learning-section-heading">
          <div>
            <h2 id="survive-heading">Survival</h2>
            <p>Keep answering—every mistake costs a life.</p>
          </div>
        </div>
        {error === 'start' && <SurviveError message="We could not start Survival." />}
        <div className="survive-start-card">
          <Clock3 aria-hidden="true" />
          <p>Take your time — there is no clock. Each wrong answer costs a life.</p>
          <button className="survive-primary" onClick={() => void start()} disabled={busy}>
            {busy ? 'Starting…' : 'Start Survival'}
          </button>
          {busy && (
            <span className="sr-only" role="status">
              Building Survival round…
            </span>
          )}
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="survive-board-heading" aria-busy={busy}>
      <p className="sr-only" role="status">
        Survival round ready.
      </p>
      <div className="survive-hud">
        <div>
          <span>Score</span>
          <strong>{run.score.toLocaleString()}</strong>
        </div>
        <div>
          <span>Combo</span>
          <strong>
            <Flame aria-hidden="true" /> ×{run.combo}
          </strong>
        </div>
        <div>
          <span>Lives</span>
          <strong>
            {Array.from({ length: run.livesMax ?? 0 }, (_, i) => (
              <Heart
                key={i}
                aria-hidden="true"
                className={
                  i < (run.livesRemaining ?? 0) ? 'survive-life--active' : 'survive-life--lost'
                }
              />
            ))}
            <span className="sr-only">
              {run.livesRemaining} of {run.livesMax} lives
            </span>
          </strong>
        </div>
      </div>

      <div className="learning-section-heading">
        <div>
          <h2 id="survive-board-heading">Stay alive</h2>
          <p>{question?.prompt ?? ''}</p>
        </div>
        <span className="learning-count">
          {answeredIds.length} of {run.totalQuestions}
        </span>
      </div>

      {grade && (
        <div
          className={`survive-feedback survive-feedback--${grade.correct ? 'correct' : 'incorrect'}`}
          role="status"
          aria-live="polite"
        >
          {grade.correct ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
          <span>
            {grade.correct
              ? `Correct · +${grade.scoreDelta} points · Combo ×${grade.combo}`
              : `Not quite. Correct answer: ${grade.correctAnswer} · Life lost`}
          </span>
        </div>
      )}

      {error === 'grade' && pending && (
        <div className="survive-recovery" role="alert">
          <p>Your answer was not confirmed. The round is paused at its last server state.</p>
          <button onClick={() => void retry()} disabled={busy}>
            Retry answer
          </button>
          <button onClick={() => void resume()} disabled={busy}>
            Resume from server
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}
      {error === 'recover' && (
        <div className="survive-recovery" role="alert">
          <p>The round could not be recovered.</p>
          <button onClick={() => void resume()} disabled={busy}>
            Try resume again
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}

      {question?.type === 'word-match' && 'options' in question && (
        <div className="survive-options" aria-label="Answer choices">
          {(question.options as string[]).map(option => (
            <button
              key={option}
              className="survive-option"
              disabled={busy}
              onClick={() => submitAnswer(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {question?.type === 'fill-in-blank' && 'sentence' in question && (
        <div className="survive-blank-card">
          <p className="survive-sentence">{question.sentence as string}</p>
          <div className="survive-input-row">
            <input
              className="survive-text-input"
              type="text"
              aria-label="Your answer"
              value={answerText}
              onChange={e => setAnswerText(e.target.value)}
              placeholder="Type your answer…"
              disabled={busy}
              autoComplete="off"
            />
            <button
              className="survive-primary"
              disabled={busy || !answerText.trim()}
              onClick={() => submitAnswer(answerText.trim())}
            >
              {busy ? 'Grading…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {!question && !summary && (
        <div className="survive-empty">
          <Sparkles aria-hidden="true" />
          <h3>All questions answered</h3>
          <button
            className="survive-primary"
            onClick={() => void completeSurvivalRun(run.runId).then(setSummary)}
            disabled={busy}
          >
            See results
          </button>
        </div>
      )}

      <button className="survive-abandon" onClick={() => void abandon()} disabled={busy}>
        End run without saving a score
      </button>
    </section>
  );
}

function SurviveError({ message }: { message: string }) {
  return (
    <div className="learning-notice learning-notice--error" role="alert">
      <XCircle aria-hidden="true" /> <span>{message}</span>
    </div>
  );
}
